'use client';

import React, { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
// --- FIX: Import ArrowRight here ---
import { ArrowRight } from 'lucide-react'; 

const GOLD_ACCENT = '#faaf40';
const DARK_GOLD = '#d4af37';
const SHADOW_GOLD = 'rgba(250, 175, 64, 0.6)';
const CARD_BG = '#1c1c1c';

// --- Custom Hook (Hydration Safe) ---
function useIsLargeScreen() {
  const [isLarge, setIsLarge] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const mediaQuery = window.matchMedia('(min-width: 1024px)');
      setIsLarge(mediaQuery.matches);
      const handler = (e) => setIsLarge(e.matches);
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    }
  }, []);

  return isLarge;
}

const equipmentData = [
  {
    title: 'EMS SCULPT (Electromagnetic)',
    image: '/ems-sculpt.jpg',
    description:
      'High-intensity electromagnetic therapy that tones muscle and sculpts the body without invasive procedures. **Achieve results faster** by targeting deep muscle fibers.',
    highlight: 'Non-Invasive Sculpting',
    reversed: false,
  },
  {
    title: 'PILATES REFORMER',
    image: '/pilates-reformer.jpg',
    description:
      'Utilize state-of-the-art Reformer machines for controlled, low-impact movements that **enhance core strength, flexibility, and posture**. Ideal for rehabilitation and alignment.',
    highlight: 'Core Strength & Alignment',
    reversed: true,
  },
  {
    title: 'EMS SUITS (Full Body Training)',
    image: '/ems-suits.jpg',
    description:
      'Wearable electric muscle stimulation suits that **intensify your workout**, activating up to 90% of your muscles simultaneously in short, efficient sessions.',
    highlight: 'Full-Muscle Activation',
    reversed: false,
  },
  {
    title: 'CARDIO PERFORMANCE SYSTEM',
    image: '/cardio-performance.jpg',
    description:
      'Experience next-level endurance training with our **smart cardio technology**, designed to monitor heart rate, oxygen efficiency, and performance metrics in real time. **Push your limits safely** and achieve optimal cardiovascular health.',
    highlight: 'Endurance & Heart Health',
    reversed: true,
  },
];

const renderDescription = (text) =>
  text.split('**').map((part, i) =>
    i % 2 === 1 ? (
      <strong key={i} className="text-white font-semibold">
        {part}
      </strong>
    ) : (
      part
    )
  );

function FeatureItem({ item }) {
  return (
    <div
      className={`flex flex-col lg:flex-row items-center gap-6 lg:gap-12 py-4 lg:py-6 ${
        item.reversed ? 'lg:flex-row-reverse' : ''
      }`}
    >
      {/* Left visual */}
      <div className="w-full lg:w-1/2 relative h-56 sm:h-72 lg:h-80 rounded-xl group overflow-hidden shadow-2xl">
        <div
          className="absolute inset-0 rounded-xl z-0"
          style={{
            background: CARD_BG,
            boxShadow: `0 0 30px rgba(0,0,0,0.8), inset 0 0 20px ${SHADOW_GOLD}`,
          }}
        />
        {/* Note: Ensure these image paths exist in your public folder */}
        <img
          src={item.image}
          alt={item.title}
          className="absolute inset-0 w-full h-full object-cover rounded-xl transition-transform duration-700 ease-out transform group-hover:scale-110 group-hover:brightness-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent lg:hidden" />
      </div>

      {/* Right content */}
      <div className="w-full lg:w-1/2 text-left space-y-3 z-10 px-2 sm:px-4 lg:px-0">
        <p
          className="text-xs sm:text-sm uppercase tracking-widest font-medium"
          style={{ color: GOLD_ACCENT }}
        >
          {item.highlight}
        </p>
        <h3 className="text-2xl sm:text-3xl lg:text-3xl font-extrabold tracking-tight text-white leading-tight">
          {item.title}
        </h3>
        <p className="text-sm sm:text-base text-gray-400 leading-relaxed pt-1">
          {renderDescription(item.description)}
        </p>
        
        {/* --- ANIMATED BUTTON --- */}
        <div className="pt-3">
          <button
            className="
              group relative px-6 py-3 sm:py-2 w-full sm:w-auto
              rounded-full border-2 border-[#faaf40]
              text-sm sm:text-md font-bold uppercase tracking-wide
              text-[#faaf40]
              overflow-hidden
              transition-all duration-300 ease-out
              hover:shadow-[0_0_25px_rgba(250,175,64,0.6)]
              hover:border-[#faaf40]
              active:scale-95
            "
          >
            {/* 1. Gradient Background (Slides Up) */}
            <span 
              className="
                absolute inset-0 w-full h-full 
                bg-gradient-to-r from-[#d4af37] to-[#faaf40] 
                translate-y-full group-hover:translate-y-0 
                transition-transform duration-300 ease-out
              "
            />

            {/* 2. Text & Icon */}
            <span className="relative z-10 flex items-center justify-center gap-2 transition-colors duration-300 group-hover:text-black">
              LEARN MORE
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}

function AnimatedCard({ item, index, total, isLarge }) {
  const ref = useRef(null);

  // -- Desktop Scroll Logic --
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  const y = useTransform(scrollYProgress, [0, 0.5, 1], ['20%', '0%', '-15%']);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.9, 1, 0.95]);
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.8, 1], [0, 1, 1, 0]);

  const mobileAnimationProps = {
    initial: { opacity: 0, y: 50 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-50px' },
    transition: { duration: 0.6, ease: 'easeOut' },
  };

  const desktopAnimationProps = {
    style: {
      y,
      scale,
      opacity,
      zIndex: total - index,
      transformOrigin: 'center center',
    },
  };

  return (
    <div
      ref={ref}
      // Compact height setting (60vh)
      className={
        isLarge
          ? 'relative min-h-[60vh] flex items-center justify-center sticky top-0'
          : 'mb-4 border-b border-white/10 pb-6 last:border-none last:pb-0 last:mb-0'
      }
    >
      <motion.div
        className={isLarge ? 'relative w-full' : 'w-full'}
        {...(isLarge ? desktopAnimationProps : mobileAnimationProps)}
      >
        <div
          className={
            isLarge
              ? 'bg-black/80 backdrop-blur-md p-6 rounded-3xl border border-white/5 shadow-2xl'
              : ''
          }
        >
          <FeatureItem item={item} />
        </div>
      </motion.div>
    </div>
  );
}

export default function PremiumEquipmentSection() {
  const isLarge = useIsLargeScreen();

  return (
    <section
      className="relative bg-gradient-to-b from-black via-[#0a0a0a] to-black text-white overflow-hidden py-10 lg:py-16"
      style={{
        perspective: '1200px',
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <div className="text-center mb-8 lg:mb-16 sticky top-0 bg-black/80 backdrop-blur-md py-4 lg:py-6 z-50 border-b border-white/5 lg:border-none rounded-b-2xl lg:rounded-none">
          <p
            className="text-xs sm:text-sm uppercase tracking-[0.2em] sm:tracking-[0.3em] font-medium mb-2"
            style={{ color: GOLD_ACCENT }}
          >
            THE NEXT GENERATION OF FITNESS
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight">
            Train with <span style={{ color: GOLD_ACCENT }}>Pure Science</span>
          </h2>
        </div>

        {/* Card Container */}
        <div className="relative flex flex-col items-center">
          {equipmentData.map((item, i) => (
            <AnimatedCard
              key={item.title}
              item={item}
              index={i}
              total={equipmentData.length}
              isLarge={isLarge}
            />
          ))}
        </div>

        {/* Spacer for desktop only */}
        {isLarge && <div className="h-[10vh]" />}
      </div>
    </section>
  );
}